import { __decorate } from "tslib";
import { Collection } from '../collection/index.js';
import { define, tools } from '../object-plus/index.js';
import { type } from '../record/index.js';
import { ItemsBehavior, transactionApi } from '../transactions.js';
import { parseReference } from './commons.js';
export function subsetOf(masterCollection, T) {
    const CollectionClass = T || Collection;
    var SubsetOf = CollectionClass._SubsetOf;
    if (!SubsetOf) {
        CollectionClass._SubsetOf = defineSubsetCollection(CollectionClass);
        SubsetOf = CollectionClass._SubsetOf;
    }
    var getMasterCollection = parseReference(masterCollection);
    return type(SubsetOf).get(function (refs) {
        !refs || refs.resolvedWith || refs.resolve(getMasterCollection(this));
        return refs;
    });
}
Collection.prototype.createSubset = function (models, options) {
    const SubsetOf = subsetOf(this, this.constructor).options.type, subset = new SubsetOf(models, options);
    subset.resolve(this);
    return subset;
};
const subsetOfBehavior = ItemsBehavior.share | ItemsBehavior.persistent;
function defineSubsetCollection(CollectionClass) {
    let SubsetOfCollection = class SubsetOfCollection extends CollectionClass {
        get __inner_state__() { return this.refs || this.models; }
        constructor(recordsOrIds, options) {
            super([], options, subsetOfBehavior);
            this.resolvedWith = null;
            this.refs = toArray(recordsOrIds);
        }
        add(a_elements, options = {}) {
            const { resolvedWith } = this, toAdd = toArray(a_elements);
            if (resolvedWith) {
                return super.add(resolveRefs(resolvedWith, toAdd), options);
            }
            else {
                if (toAdd.length) {
                    const isRoot = transactionApi.begin(this);
                    this.refs = this.refs ? this.refs.concat(toAdd) : toAdd.slice();
                    transactionApi.markAsDirty(this, options);
                    isRoot && transactionApi.commit(this);
                }
            }
        }
        reset(a_elements, options = {}) {
            const { resolvedWith } = this, elements = toArray(a_elements);
            return resolvedWith ?
                super.reset(resolveRefs(resolvedWith, elements), options) :
                delaySet(this, elements, options) || [];
        }
        _createTransaction(a_elements, options) {
            const { resolvedWith } = this, elements = toArray(a_elements);
            return resolvedWith ?
                super._createTransaction(resolveRefs(resolvedWith, elements), options) :
                delaySet(this, elements, options);
        }
        toJSON() {
            return this.refs ?
                this.refs.map(objOrId => objOrId.id || objOrId) :
                this.models.map(model => model.id);
        }
        _validateNested() { return 0; }
        get length() {
            return this.models.length || (this.refs ? this.refs.length : 0);
        }
        clone(owner) {
            var Ctor = this.constructor, copy = new Ctor([], {
                model: this.model,
                comparator: this.comparator
            });
            if (this.resolvedWith) {
                copy.resolvedWith = this.resolvedWith;
                copy.refs = null;
                copy.reset(this.models, { silent: true });
            }
            else {
                copy.refs = this.refs.slice();
            }
            return copy;
        }
        parse(raw) {
            return raw;
        }
        resolve(collection) {
            if (collection && collection.length) {
                this.resolvedWith = collection;
                if (this.refs) {
                    this.reset(this.refs, { silent: true });
                    this.refs = null;
                }
            }
            return this;
        }
        getModelIds() { return this.toJSON(); }
        toggle(modelOrId, val) {
            return super.toggle(this.resolvedWith.get(modelOrId), val);
        }
        addAll() {
            if (this.resolvedWith) {
                this.set(this.resolvedWith.models);
                return this.models;
            }
            throw new Error("Cannot add elemens because the subset collection is not resolved yet.");
        }
        toggleAll() {
            return this.length ? this.reset() : this.addAll();
        }
    };
    SubsetOfCollection = __decorate([
        define
    ], SubsetOfCollection);
    SubsetOfCollection.prototype._itemEvents = void 0;
    return SubsetOfCollection;
}
function resolveRefs(master, elements) {
    const records = [];
    for (let el of elements) {
        const record = master.get(el);
        if (record)
            records.push(record);
    }
    return records;
}
function delaySet(collection, elements, options) {
    if (tools.notEqual(collection.refs, elements)) {
        const isRoot = transactionApi.begin(collection);
        collection.refs = elements.slice();
        transactionApi.markAsDirty(collection, options);
        isRoot && transactionApi.commit(collection);
    }
}
function toArray(elements) {
    return elements ? (Array.isArray(elements) ? elements : [elements]) : [];
}
//# sourceMappingURL=subsetOf.js.map